import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TagApi } from 'app/modules/browse/api/tag.api';
import { VehicleApi } from 'app/modules/browse/api/vehicle.api';
import { VideoApi } from 'app/modules/browse/api/video.api';
import { Tag } from 'app/modules/browse/models/tag.interface';
import { Vehicle } from 'app/modules/browse/models/vehicle.interface';
import { BehaviorSubject, Subscription, forkJoin } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { TagChipComponent } from './tag-chip/tag-chip.component';
import { RippleModule } from 'primeng/ripple';
import { NgClass, AsyncPipe, TitleCasePipe } from '@angular/common';
import { ResponsiveService } from 'app/services/responsive.service';
import {
  ButtonComponent,
  Coaching,
  GpsOnly,
  Information,
  VideoSection,
  Workout,
} from '@kinomap-dev/ng-ui-lib';
import { ToasterService } from 'app/services/toaster.service';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { EditorConfig } from 'ckeditor5';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { CKEditorConfig } from 'app/configs/ckeditorConfig';

@Component({
  selector: 'app-edit-scenic',
  templateUrl: './edit-scenic.component.html',
  styleUrls: ['./edit-scenic.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    ButtonComponent,
    RippleModule,
    TagChipComponent,
    AsyncPipe,
    CKEditorModule,
    TitleCasePipe,
    TranslateModule,
  ],
})
export class EditScenicComponent implements OnInit, OnDestroy {
  private tagsApi: TagApi = inject(TagApi);
  private vehiculeApi: VehicleApi = inject(VehicleApi);
  private toasterService: ToasterService = inject(ToasterService);
  private videoApi: VideoApi = inject(VideoApi);
  private responsiveService: ResponsiveService = inject(ResponsiveService);

  @Input() editType!: 'scenic' | 'coaching';
  @Input() videoId!: number;
  @Input() information: Information;
  @Input() loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );

  @Output() cancelClick: EventEmitter<MouseEvent> =
    new EventEmitter<MouseEvent>();
  @Output() postChanges: EventEmitter<any> = new EventEmitter<any>();
  subscriptions: Subscription[] = [];

  editForm: FormGroup | null = null;

  editGpsOnlyForm: FormGroup = new FormGroup({});
  editWorkoutForm: FormGroup = new FormGroup({});
  editCoachingForm: FormGroup = new FormGroup({});
  editScenicForm: FormGroup = new FormGroup({});

  currentScenicTags: Tag[] = [];
  tags: Tag[] = [];
  allTags: Tag[] = [];
  editTags: boolean = false;
  vehicules: Vehicle[] = [];
  langs: any[] = [];

  editor: typeof ClassicEditor = ClassicEditor;
  config: EditorConfig = {};

  // Responsive variables
  xSmallScreen$: BehaviorSubject<boolean> =
    this.responsiveService.xSmallScreen$;
  smallScreen$: BehaviorSubject<boolean> = this.responsiveService.smallScreen$;
  mediumScreen$: BehaviorSubject<boolean> =
    this.responsiveService.mediumScreen$;
  largeScreen$: BehaviorSubject<boolean> = this.responsiveService.largeScreen$;
  xLargeScreen$: BehaviorSubject<boolean> =
    this.responsiveService.xLargeScreen$;

  video_tags: number[] = [];

  private updateTagsFormControl(markAsDirty: boolean = false): void {
    if (this.editType !== 'scenic') {
      return;
    }

    const tagsControl: AbstractControl = this.editScenicForm.get('tags');
    if (!tagsControl) {
      return;
    }

    this.allTags = this.allTags.filter(
      (tag: Tag) => !this.tags.find((activeTag: Tag) => activeTag.id === tag.id)
    );

    tagsControl.setValue(this.tags.map((tag: Tag) => tag.id));
    if (markAsDirty) {
      tagsControl.markAsDirty();
      tagsControl.markAsTouched();
    }
    tagsControl.updateValueAndValidity({ emitEvent: false });
  }

  onSubmit(): void {
    this.loading$.next(true);
    this.editTags = false;
    this.postChanges.emit(this.editForm.value);
  }

  onAddableClick(tag: Tag): void {
    if (this.allTags.find((t: Tag) => t === tag)) {
      this.allTags = this.allTags.filter((t: Tag) => t !== tag);
      this.tags = [...this.tags, tag];
      this.updateTagsFormControl(true);
    }
  }

  onSelectTagClick(tag: Tag) {
    if (this.tags.find((t: Tag) => t === tag)) {
      this.tags = this.tags.filter((t: Tag) => t !== tag);
      this.allTags = [...this.allTags, tag];
      this.updateTagsFormControl(true);
    }
  }

  includesTag(tag: Tag): boolean {
    return this.currentScenicTags.includes(tag);
  }

  cancelForm(event: MouseEvent): void {
    this.cancelClick.emit(event);
  }

  ngOnInit(): void {
    this.config = CKEditorConfig;
    switch (this.information.type) {
      case 'scenic':
        const scenic: VideoSection = this.information as VideoSection;
        this.editForm = this.editScenicForm;
        this.editScenicForm.addControl('title', new FormControl(scenic.name));
        this.editScenicForm.addControl(
          'vehicule_id',
          new FormControl(scenic.vehicule_id)
        );
        this.editScenicForm.addControl(
          'description',
          new FormControl(scenic.description)
        );
        this.editScenicForm.addControl(
          'status',
          new FormControl(scenic.status)
        );
        this.editScenicForm.addControl(
          'recommended_mode',
          new FormControl(scenic.recommended_mode ?? null)
        );
        this.editScenicForm.addControl(
          'recommended_audio',
          new FormControl(scenic.recommended_audio ?? null)
        );
        this.editScenicForm.addControl(
          'comment_available',
          new FormControl(true)
        );
        this.editScenicForm.addControl(
          'tags',
          new FormControl(scenic.tags.map((tag: Tag) => tag.id))
        );
        this.currentScenicTags = [...scenic.tags];
        break;

      case 'workout':
        const workout: Workout = this.information as Workout;
        this.editForm = this.editWorkoutForm;
        this.editWorkoutForm.addControl('title', new FormControl(workout.name));
        this.editWorkoutForm.addControl(
          'description',
          new FormControl(workout.description)
        );
        this.editScenicForm.addControl(
          'status',
          new FormControl(workout.status)
        );
        break;

      case 'gpsOnly':
        const gpsOnly: GpsOnly = this.information as GpsOnly;
        this.editForm = this.editGpsOnlyForm;
        this.editGpsOnlyForm.addControl('title', new FormControl(gpsOnly.name));
        this.editGpsOnlyForm.addControl(
          'description',
          new FormControl(gpsOnly.description)
        );
        this.editGpsOnlyForm.addControl(
          'status',
          new FormControl(gpsOnly.status)
        );
        break;

      case 'coaching':
        const coaching: Coaching = this.information as Coaching;
        this.editForm = this.editCoachingForm;
        this.editCoachingForm.addControl(
          'title',
          new FormControl(coaching.name)
        );
        this.editCoachingForm.addControl(
          'description',
          new FormControl(coaching.description)
        );
        this.editCoachingForm.addControl(
          'status',
          new FormControl(coaching.status)
        );
        this.editCoachingForm.addControl(
          'lang',
          new FormControl(coaching.lang.id)
        );
        this.editCoachingForm.addControl(
          'recommended_mode',
          new FormControl(coaching.recommended_mode)
        );
        this.editCoachingForm.addControl(
          'recommended_audio',
          new FormControl(coaching.recommended_audio)
        );
        this.editCoachingForm.addControl(
          'hardware',
          new FormControl(coaching.hardware)
        );
        this.editCoachingForm.addControl(
          'equipment_type',
          new FormControl(coaching.equipment_type)
        );
        this.editCoachingForm.addControl(
          'comment_available',
          new FormControl(true)
        );
        break;
    }

    switch (this.editType) {
      case 'scenic':
        this.subscriptions = [
          ...this.subscriptions,
          forkJoin([
            this.vehiculeApi.getVehicles$(),
            this.tagsApi.getTags$(),
          ]).subscribe({
            next: (res: any) => {
              this.vehicules = res[0];
              this.allTags = res[1];
              this.tags = [...this.tags, ...this.currentScenicTags];
              this.updateTagsFormControl();
            },
            error: (err) => {
              console.error(err);
            },
            complete: () => {
              this.currentScenicTags = [
                ...this.currentScenicTags,
                ...this.allTags.filter((tag: Tag) =>
                  this.video_tags.includes(tag.id)
                ),
              ];
            },
          }),
        ];
        break;

      case 'coaching':
        this.subscriptions = [
          ...this.subscriptions,
          forkJoin([this.videoApi.getAllCoachingLang()]).subscribe({
            next: (res: any) => {
              this.langs = res[0].data;
            },
            error: (err) => {
              this.cancelClick.emit();
              this.toasterService.error(
                null,
                'Oups ! une erreur est survenu. Veuillez réessayer plus tard ...'
              );
            },
          }),
        ];
        break;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }
}
